import { useState, useEffect, useCallback } from 'react';
import { useAccount, useSignMessage } from 'wagmi';
import { message } from 'antd';
import { Web3AuthState, User } from '../types/auth';
import { ddddAlertApi } from '../utils/ddddAlertApi';

/**
 * Web3认证Hook
 * 处理钱包连接、消息签名、用户注册/登录逻辑
 */
export const useWeb3Auth = () => {
  const { address, isConnected } = useAccount();
  const { signMessageAsync } = useSignMessage();

  const [authState, setAuthState] = useState<Web3AuthState>({
    isAuthenticated: false,
    user: null,
    token: null,
    apiKey: null,
    loading: false,
    error: null,
  });

  // 清除认证状态
  const clearAuthState = useCallback(() => {
    localStorage.removeItem('dddd_auth_token');
    localStorage.removeItem('dddd_auth_user');
    localStorage.removeItem('dddd_auth_api_key');
    setAuthState({
      isAuthenticated: false,
      user: null,
      token: null,
      apiKey: null,
      loading: false,
      error: null,
    });
    ddddAlertApi.setToken('');
    ddddAlertApi.setApiKey('');
  }, []);

  // 从localStorage恢复认证状态
  useEffect(() => {
    const savedToken = localStorage.getItem('dddd_auth_token');
    const savedUser = localStorage.getItem('dddd_auth_user');
    const savedApiKey = localStorage.getItem('dddd_auth_api_key');

    if (savedToken && savedUser) {
      try {
        const user = JSON.parse(savedUser);
        setAuthState(prev => ({
          ...prev,
          isAuthenticated: true,
          user,
          token: savedToken,
          apiKey: savedApiKey,
        }));
        ddddAlertApi.setToken(savedToken);
        if (savedApiKey) {
          ddddAlertApi.setApiKey(savedApiKey);
        }
      } catch (error) {
        console.error('Failed to restore auth state:', error);
        clearAuthState();
      }
    }
  }, [clearAuthState]);

  // 保存认证状态
  const saveAuthState = useCallback((user: User, token: string) => {
    localStorage.setItem('dddd_auth_token', token);
    localStorage.setItem('dddd_auth_user', JSON.stringify(user));
    localStorage.setItem('dddd_auth_api_key', user.api_key);
    
    setAuthState({
      isAuthenticated: true,
      user,
      token,
      apiKey: user.api_key,
      loading: false,
      error: null,
    });
    
    ddddAlertApi.setToken(token);
    ddddAlertApi.setApiKey(user.api_key);
  }, []);

  // 获取服务器提供的签名消息
  const getSignMessage = useCallback(async (address: string): Promise<string> => {
    try {
      const result = await ddddAlertApi.getWeb3Message(address);
      if (result.success && result.data) {
        return result.data.message;
      }
      throw new Error('获取签名消息失败');
    } catch (error) {
      console.error('Failed to get sign message:', error);
      throw new Error('获取签名消息失败，请重试');
    }
  }, []);

  // Web3签名认证
  const signAndAuth = useCallback(async (): Promise<boolean> => {
    if (!address || !isConnected) {
      message.error('请先连接钱包');
      return false;
    }

    setAuthState(prev => ({ ...prev, loading: true, error: null }));

    try {
      // 1. 获取服务器提供的签名消息
      message.info('正在获取签名消息...');
      const signMessage = await getSignMessage(address);

      // 2. 请求用户签名
      message.info('请在钱包中签名以验证身份...');
      const signature = await signMessageAsync({ message: signMessage });

      if (!signature) {
        throw new Error('签名失败');
      }

      // 3. 准备Web3认证数据
      const authData = {
        address: address.toLowerCase(),
        signature,
        message: signMessage,
      };

      try {
        // 先尝试登录
        const loginResult = await ddddAlertApi.web3Login(authData);

        if (loginResult.success && loginResult.data) {
          saveAuthState(loginResult.data.user, loginResult.data.token);
          message.success('登录成功！');
          return true;
        }
      } catch (loginError) {
        console.log('Login failed, trying registration:', loginError);

        // 登录失败，尝试注册
        try {
          const registerResult = await ddddAlertApi.web3Register(authData);

          if (registerResult.success && registerResult.data) {
            saveAuthState(registerResult.data.user, registerResult.data.token);
            message.success('注册成功！');
            return true;
          }
        } catch (registerError) {
          console.error('Registration failed:', registerError);
          throw new Error('注册失败，请重试');
        }
      }

      throw new Error('认证失败');
    } catch (error: any) {
      console.error('Web3 Auth Error:', error);
      const errorMessage = error.message || '认证失败，请重试';
      setAuthState(prev => ({ ...prev, loading: false, error: errorMessage }));
      message.error(errorMessage);
      return false;
    }
  }, [address, isConnected, signMessageAsync, getSignMessage, saveAuthState]);

  // 登出
  const logout = useCallback(() => {
    clearAuthState();
    message.success('已退出登录');
  }, [clearAuthState]);

  // 刷新API密钥
  const refreshApiKey = useCallback(async (): Promise<boolean> => {
    if (!authState.isAuthenticated) {
      message.error('请先登录');
      return false;
    }

    try {
      setAuthState(prev => ({ ...prev, loading: true }));
      
      const result = await ddddAlertApi.refreshApiKey();
      
      if (result.success && result.data) {
        const newApiKey = result.data.api_key;
        
        // 更新状态和本地存储
        localStorage.setItem('dddd_auth_api_key', newApiKey);
        setAuthState(prev => ({
          ...prev,
          apiKey: newApiKey,
          loading: false,
          user: prev.user ? { ...prev.user, api_key: newApiKey } : null,
        }));
        
        ddddAlertApi.setApiKey(newApiKey);
        message.success('API密钥已刷新');
        return true;
      }
      
      throw new Error('刷新API密钥失败');
    } catch (error: any) {
      console.error('Refresh API Key Error:', error);
      setAuthState(prev => ({ ...prev, loading: false }));
      message.error(error.message || '刷新API密钥失败');
      return false;
    }
  }, [authState.isAuthenticated]);

  // 验证当前认证状态
  const validateAuth = useCallback(async (): Promise<boolean> => {
    if (!authState.token) {
      return false;
    }

    try {
      const result = await ddddAlertApi.getProfile();
      return result.success && !!result.data;
    } catch (error: any) {
      console.error('Auth validation failed:', error);

      // 如果是Token无效错误，清除认证状态
      if (error.message?.includes('Invalid access token') || error.message?.includes('INVALID_TOKEN')) {
        clearAuthState();
        message.error('登录已过期，请重新连接钱包进行认证');
      }

      return false;
    }
  }, [authState.token, clearAuthState]);

  return {
    // 状态
    ...authState,
    isWalletConnected: isConnected,
    walletAddress: address,
    
    // 方法
    signAndAuth,
    logout,
    refreshApiKey,
    validateAuth,
    clearError: () => setAuthState(prev => ({ ...prev, error: null })),
  };
};
